
import { 
  MelodyName, BallSoundEffect, SimulationConfig, MelodyInstrument, 
  MelodyData, RhythmicNote, MidiData, MidiNote, MidiNoteAction, PlaylistItem 
} from '../types';
import { NOTES, MELODIES_RAW } from '../audioData';
import { SynthEngine } from './SynthEngine';
import { MidiParser } from './MidiParser';
import { AudioSlicer } from './AudioSlicer';
import { AudioRack, StudioRack } from './AudioRack';
import { FrequencyAnalyzer } from './FrequencyAnalyzer';
import { AudioBufferLoader } from './AudioBufferLoader';

const MELODIES: Record<string, MelodyData> = {};
Object.entries(MELODIES_RAW).forEach(([name, notes]) => {
  MELODIES[name] = notes as MelodyData;
});

export class AudioService {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private noteIndex: number = 0;
  private activeVoices: number = 0;
  private readonly MAX_VOICES = 256; 
  private lastSlidingTick: number = 0;
  
  private melodyRack: StudioRack | null = null;
  private fxRack: StudioRack | null = null;
  private analyser: AnalyserNode | null = null;
  private freqData: Uint8Array | null = null;

  private synthEngine: SynthEngine | null = null;
  private audioSlicer: AudioSlicer = new AudioSlicer();

  private lastBreakSource: AudioScheduledSourceNode | null = null;
  private lastBreakGain: GainNode | null = null;
  private lastForceTriggerTime: number = 0;
  private forceTriggerCount: number = 0;

  constructor() {}

  public resume() { this.initCtx(); }

  public initCtx() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 'interactive' });
      this.masterGain = this.ctx.createGain();
      this.limiter = this.ctx.createDynamicsCompressor();
      this.limiter.threshold.setValueAtTime(-0.5, this.ctx.currentTime);
      this.limiter.ratio.setValueAtTime(20, this.ctx.currentTime);
      this.limiter.attack.setValueAtTime(0.002, this.ctx.currentTime);
      this.limiter.release.setValueAtTime(0.25, this.ctx.currentTime);
      
      this.melodyRack = AudioRack.create(this.ctx, this.masterGain);
      this.fxRack = AudioRack.create(this.ctx, this.masterGain);

      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
      
      this.masterGain.connect(this.analyser);
      this.analyser.connect(this.limiter);
      this.limiter.connect(this.ctx.destination);

      this.setupReverbBuffers();
      this.synthEngine = new SynthEngine(this.createNoiseBuffer(this.ctx));
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  private createNoiseBuffer(ctx: AudioContext): AudioBuffer {
      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const out = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) out[i] = Math.random() * 2 - 1;
      return buffer;
  }

  private setupReverbBuffers() {
    if (!this.ctx || !this.melodyRack || !this.fxRack) return;
    const len = this.ctx.sampleRate * 2.5; 
    const buf = this.ctx.createBuffer(2, len, this.ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 4);
    }
    this.melodyRack.reverbNode.buffer = buf;
    this.fxRack.reverbNode.buffer = buf;
  }

  public resetIndex() { this.noteIndex = 0; }
  public resetEcho() {
    if (!this.ctx) return;
    if (this.melodyRack) AudioRack.reset(this.melodyRack, this.ctx);
    if (this.fxRack) AudioRack.reset(this.fxRack, this.ctx);
  }
  public resetSongCursor() { this.audioSlicer.reset(); }

  public getFrequencyData(): Uint8Array {
    if (this.analyser && this.freqData) {
      this.analyser.getByteFrequencyData(this.freqData as any);
      return this.freqData;
    }
    return new Uint8Array(0);
  }

  public getAvailableVoicesRaw() { return this.MAX_VOICES - this.activeVoices; }

  public async uploadSongForSlicing(file: File) {
      const ctx = this.initCtx();
      const ab = await file.arrayBuffer();
      try {
          const buffer = await ctx.decodeAudioData(ab);
          this.audioSlicer.setBuffer(buffer);
          return buffer.duration;
      } catch (e) { console.error("Failed to decode song for slicing", e); return 0; }
  }

  public getSongPeaks(threshold: number = 0.5): number[] { return this.audioSlicer.getPeaks(threshold); }

  public playNextSlice(config: SimulationConfig) {
      const ctx = this.initCtx();
      const slice = this.audioSlicer.calculateNextSlice(config, ctx.currentTime);
      if (!slice) return;
      const source = ctx.createBufferSource();
      // @ts-ignore
      source.buffer = this.audioSlicer.songBuffer;
      source.playbackRate.value = slice.playbackRate;
      const gain = ctx.createGain();
      AudioRack.update(this.melodyRack!, ctx, this.masterGain!, config, true);
      source.connect(gain); gain.connect(this.melodyRack!.distortionNode);
      gain.gain.setValueAtTime(0, slice.playStartTime);
      gain.gain.linearRampToValueAtTime(config.melodyVolume, slice.playStartTime + 0.005);
      gain.gain.setValueAtTime(config.melodyVolume, slice.playStartTime + slice.actualDuration - 0.005);
      gain.gain.linearRampToValueAtTime(0, slice.playStartTime + slice.actualDuration);
      source.start(slice.playStartTime, slice.startTimeOffset, slice.actualDuration);
      source.stop(slice.playStartTime + slice.actualDuration + 0.1);
      this.activeVoices++;
      setTimeout(() => { this.activeVoices = Math.max(0, this.activeVoices - 1); }, (slice.playStartTime - ctx.currentTime + slice.actualDuration) * 1000 + 100);
  }

  public playSliceAtIndex(index: number, config: SimulationConfig) {
      const slice = this.audioSlicer.getSliceAtIndex(index, config);
      if (!slice) return;
      const ctx = this.initCtx();
      const source = ctx.createBufferSource();
      // @ts-ignore
      source.buffer = this.audioSlicer.songBuffer;
      const gain = ctx.createGain(); gain.gain.value = config.melodyVolume;
      source.connect(gain);
      AudioRack.update(this.melodyRack!, ctx, this.masterGain!, config, true);
      gain.connect(this.melodyRack!.distortionNode);
      source.start(ctx.currentTime, slice.startTime, slice.duration);
      source.stop(ctx.currentTime + slice.duration + 0.1);
  }

  public playNote(melodyName: MelodyName, config: SimulationConfig, baseVolume: number = 1.0, force: boolean = false, startTime?: number) {
    const ctx = this.initCtx();
    const now = ctx.currentTime;
    if (force) {
        if (now - this.lastForceTriggerTime < 0.01) {
            this.forceTriggerCount++; if (this.forceTriggerCount > 3) return;
        } else { this.lastForceTriggerTime = now; this.forceTriggerCount = 1; }
    }
    const melody = MELODIES[melodyName] || config.customMelodies.find(m => m.name === melodyName || m.id === melodyName)?.frequencies || (melodyName === 'Custom (Uploaded)' ? config.customFrequencies : undefined);
    if (!melody || melody.length === 0) return;
    const data = melody[this.noteIndex % melody.length];
    this.noteIndex++;
    const play = (f: number) => {
        const instr = (config.universalForceInstrument && typeof config.universalForceInstrument === 'string') ? (config.universalForceInstrument as MelodyInstrument) : (config.melodyInstrument || 'GRAND_PIANO');
        const vol = baseVolume * config.melodyVolume;
        AudioRack.update(this.melodyRack!, ctx, this.masterGain!, config, true);
        this.synthEngine?.synthTone(ctx, f, startTime || now, instr, vol, this.melodyRack!.distortionNode, config.audioTrailEffect, undefined, config.universalMasterPitchShift || config.audioPitchShift);
    };
    if (Array.isArray(data)) data.forEach(f => play(f));
    else if (typeof data === 'number') play(data);
    else if (typeof data === 'object' && 'notes' in data) {
        const notes = Array.isArray(data.notes) ? data.notes : [data.notes];
        notes.forEach(f => play(f));
    }
  }

  public async playSoundEffect(effect: BallSoundEffect, config: SimulationConfig, volume: number = 1.0, isBreak: boolean = false) {
    if (effect === 'NONE') return;
    const ctx = this.initCtx();
    const custom = config.userSounds.find(s => s.name === effect || s.id === effect);
    if (custom && (custom.url || custom.base64)) {
        await this.playCustomBuffer(custom.url || custom.base64!, volume, config, isBreak);
        return;
    }
    const freq = NOTES[effect as keyof typeof NOTES];
    if (freq) {
        const dest = isBreak ? this.fxRack!.distortionNode : this.melodyRack!.distortionNode;
        AudioRack.update(isBreak ? this.fxRack! : this.melodyRack!, ctx, this.masterGain!, config, !isBreak);
        this.synthEngine?.synthTone(ctx, freq, ctx.currentTime, 'GRAND_PIANO', volume * config.fxVolume, dest, false);
    }
  }

  public async playCustomBuffer(source: string, volume: number, config: SimulationConfig, isBreak: boolean = false, force: boolean = false) {
    const ctx = this.initCtx();
    const now = ctx.currentTime;
    if (force) {
        if (now - this.lastForceTriggerTime < 0.01) {
            this.forceTriggerCount++; if (this.forceTriggerCount > 3) return;
        } else { this.lastForceTriggerTime = now; this.forceTriggerCount = 1; }
    }
    const maxVoices = config.audioCompoundMode ? 256 : 128;
    if (!force && this.activeVoices >= maxVoices) return;
    const buf = await AudioBufferLoader.load(ctx, source);
    if (!buf) return;
    this.activeVoices++;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const gain = ctx.createGain(); src.playbackRate.value = config.audioCompoundMode ? 0.95 + Math.random() * 0.1 : 1.0;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    if (config.audioCutoffEnabled && force && this.lastBreakSource) {
        try {
            this.lastBreakGain?.gain.cancelScheduledValues(ctx.currentTime);
            this.lastBreakGain?.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.02);
            this.lastBreakSource.stop(ctx.currentTime + 0.03);
        } catch(e) {}
    }
    if (config.audioCutoffEnabled && force) { this.lastBreakSource = src; this.lastBreakGain = gain; }
    src.connect(gain);
    const rack = isBreak ? this.fxRack! : this.melodyRack!;
    AudioRack.update(rack, ctx, this.masterGain!, config, !isBreak);
    gain.connect(rack.distortionNode);
    src.start();
    src.onended = () => { this.activeVoices = Math.max(0, this.activeVoices - 1); };
  }

  public playMidiNote(midiNote: number, config: SimulationConfig, baseVolume: number = 1.0, startTime?: number, duration?: number) {
    if (this.activeVoices >= this.MAX_VOICES) return;
    const ctx = this.initCtx();
    const now = startTime !== undefined ? startTime : ctx.currentTime;
    const freq = 440 * Math.pow(2, (midiNote - 69) / 12);
    const instr = (config.universalForceInstrument && typeof config.universalForceInstrument === 'string') ? (config.universalForceInstrument as MelodyInstrument) : (config.melodyInstrument || 'GRAND_PIANO');
    const vol = baseVolume * config.melodyVolume * 2.0;
    AudioRack.update(this.melodyRack!, ctx, this.masterGain!, config, true);
    this.activeVoices++;
    const dur = duration !== undefined ? duration : (config.audioTrailEffect ? 2.5 : 0.8);
    this.synthEngine?.synthTone(ctx, freq, now, instr, vol, this.melodyRack!.distortionNode, config.audioTrailEffect, duration, config.universalMasterPitchShift || config.audioPitchShift);
    setTimeout(() => { this.activeVoices = Math.max(0, this.activeVoices - 1); }, (now - ctx.currentTime + dur) * 1000 + 100);
  }

  public playMidiNotes(notes: { midi: number, velocity: number, time: number, duration?: number }[], config: SimulationConfig) {
      notes.forEach(n => this.playMidiNote(n.midi, config, n.velocity, this.initCtx().currentTime + n.time, n.duration));
  }

  public playRhythmicStep(notes: { midi: number, velocity: number, delay: number, duration?: number }[], config: SimulationConfig, startTime?: number, timeScale: number = 1.0) {
      const start = startTime !== undefined ? startTime : this.initCtx().currentTime;
      notes.forEach(n => { this.playMidiNote(n.midi, config, n.velocity, start + (n.delay / (timeScale || 1.0) / 1000), n.duration ? (n.duration / (timeScale || 1.0)) : undefined); });
  }

  public playCollisionSound(intensity: number, config: SimulationConfig) {
      if (config.ballCollisionSoundType === 'NONE') return;
      this.playSoundEffect(config.selectedBallSoundEffect, config, intensity * (config.masterSpeed || 1.0));
  }

  public playSlidingSound(velocity: number, config: SimulationConfig) {
      if (!config.audioSlidingEnabled) return;
      const ctx = this.initCtx();
      const speed = Math.min(velocity / 10, 1.0);
      const interval = (1.0 / config.audioSlidingMaxSpeed) / Math.max(speed, 0.1);
      if (ctx.currentTime * 1000 - this.lastSlidingTick < interval) return;
      this.lastSlidingTick = ctx.currentTime * 1000;
      this.synthEngine?.synthTone(ctx, (200 + speed * 1500) * config.audioSlidingPitch, ctx.currentTime, 'GRAND_PIANO', config.audioSlidingVolume * config.melodyVolume, this.melodyRack!.distortionNode, false, 0.05);
  }

  public parseMidiFile(file: File) { return MidiParser.parseMidiFile(file); }
  public parseMidiBase64(base64: string, name?: string) { return MidiParser.parseMidiBase64(base64, name || 'Pasted MIDI'); }
  public getMidiTracks(data: MidiData) { return MidiParser.getMidiTracks(data); }
  public parseOnlineSequencerString(input: string) { return MidiParser.parseOnlineSequencerString(input); }

  public getAvailableVoices(melodyStr: string): string[] { return this.synthEngine?.getAvailableVoices(melodyStr) || []; }
  public parseMelodyString(melodyStr: string, voice?: string) { return this.synthEngine?.parseMelodyString(melodyStr, voice) || []; }

  public getNoteAction(note: MidiNote, config: SimulationConfig, overrideFilteredTracks?: number[], overrideBackgroundTracks?: number[], overrideMutedTracks?: number[]) {
      if (!config.rhythmicMidiFilterEnabled) return MidiNoteAction.BOUNCE;
      const mutedTracks = overrideMutedTracks || config.rhythmicMidiMutedTracks || [];
      if (mutedTracks.length > 0 && note.track !== undefined && mutedTracks.includes(note.track)) return MidiNoteAction.NONE;
      if (note.velocity * 127 < config.rhythmicMidiMinVelocity || note.velocity * 127 > config.rhythmicMidiMaxVelocity) return MidiNoteAction.NONE;
      if (note.midi < config.rhythmicMidiMinPitch || note.midi > config.rhythmicMidiMaxPitch) return MidiNoteAction.NONE;
      const durMs = note.duration * 1000;
      if (durMs < config.rhythmicMidiMinDuration || durMs > config.rhythmicMidiMaxDuration) return MidiNoteAction.NONE;
      if (config.rhythmicMidiMuteBass && note.midi < 48) return MidiNoteAction.NONE;
      if (config.rhythmicMidiMuteMain && note.midi > 72) return MidiNoteAction.NONE;
      const filtered = overrideFilteredTracks || config.rhythmicMidiFilteredTracks || [];
      if (filtered.length > 0 && note.track !== undefined && !filtered.includes(note.track)) {
        if (config.rhythmicMidiPlayBackground) {
            const bg = overrideBackgroundTracks || config.rhythmicMidiBackgroundTracks || [];
            if (bg.length > 0 && note.track !== undefined && bg.includes(note.track)) return MidiNoteAction.PLAY;
            else if (bg.length === 0) return MidiNoteAction.PLAY;
        }
        return MidiNoteAction.NONE;
      }
      return MidiNoteAction.BOUNCE;
  }

  public getNextRhythmicStep(melodyName: MelodyName, config: SimulationConfig, currentIndex: number, trackOverrides?: any) {
    let currentMelodyName = melodyName;
    if (config.playlistEnabled && config.playlist.length > 0) {
        const item = config.playlist[config.playlistCurrentIndex % config.playlist.length];
        if (item) currentMelodyName = item.melodyId;
    }
    const customMelody = config.customMelodies.find(m => m.id === currentMelodyName || m.name === currentMelodyName);
    if (customMelody?.format === 'MIDI' && customMelody.midiData) {
        const allNotes = customMelody.midiData.notes;
        if (allNotes.length === 0) return { totalDuration: 300, notesToPlay: [], nextIndex: currentIndex };
        let foundNextBounce = false, searchCount = 0, notesToPlay: any[] = [], totalDuration = 0, nextIndex = currentIndex, playlistAdvance = false;
        while (!foundNextBounce && searchCount < allNotes.length) {
            const currentNote = allNotes[nextIndex % allNotes.length], currentTime = currentNote.time, chordNotes: MidiNote[] = [];
            let i = nextIndex % allNotes.length;
            while (i < allNotes.length && Math.abs(allNotes[i].time - currentTime) < 0.03) { chordNotes.push(allNotes[i]); i++; }
            const chordActions = chordNotes.map(n => this.getNoteAction(n, config, trackOverrides?.filtered, trackOverrides?.background, trackOverrides?.muted));
            const passesFilter = chordActions.some(a => a >= MidiNoteAction.BOUNCE);
            let stepDuration = i < allNotes.length ? (allNotes[i].time - currentTime) * 1000 : (config.playlistEnabled && config.playlist.length > 1 ? (customMelody.midiData.duration - currentTime) * 1000 + (config.playlistTransitionTime || 0) : (customMelody.midiData.duration - currentTime + allNotes[0].time) * 1000);
            if (i >= allNotes.length && config.playlistEnabled && config.playlist.length > 1) playlistAdvance = true;
            if (passesFilter && searchCount > 0) foundNextBounce = true;
            else {
                chordNotes.forEach((n, idx) => { if (chordActions[idx] >= MidiNoteAction.PLAY) notesToPlay.push({ midi: n.midi, velocity: n.velocity, delay: totalDuration, duration: config.rhythmicMidiUseExactDuration ? n.duration : undefined }); });
                totalDuration += Math.max(10, stepDuration); nextIndex = i % allNotes.length;
                if (playlistAdvance) foundNextBounce = true;
            }
            searchCount += chordNotes.length;
        }
        return { totalDuration: Math.max(10, totalDuration), notesToPlay, nextIndex, playlistAdvance };
    }
    const melody = MELODIES[melodyName] || customMelody?.frequencies || (melodyName === 'Custom (Uploaded)' ? config.customFrequencies : undefined);
    if (!melody || melody.length === 0) return { totalDuration: 300, notesToPlay: [], nextIndex: currentIndex };
    const rawData = melody[currentIndex % melody.length];
    let noteData = (typeof rawData === 'object' && !Array.isArray(rawData)) ? (rawData as RhythmicNote).notes : rawData as (number | number[]);
    let duration = (typeof rawData === 'object' && !Array.isArray(rawData)) ? (rawData as RhythmicNote).duration || 300 : 300, notesToPlayRes: any[] = [];
    (Array.isArray(noteData) ? noteData : [noteData]).forEach(f => { notesToPlayRes.push({ midi: Math.round(12 * Math.log2(f / 440) + 69), velocity: 0.8, delay: 0 }); });
    return { totalDuration: duration, notesToPlay: notesToPlayRes, nextIndex: (currentIndex + 1) % melody.length };
  }

  public getPlaylistTotalDuration(playlist: PlaylistItem[], config: SimulationConfig) {
      let total = 0;
      playlist.forEach(item => {
          const custom = config.customMelodies.find(m => m.id === item.melodyId || m.name === item.melodyId);
          total += (custom?.format === 'MIDI' && custom.midiData) ? custom.midiData.duration * 1000 : 5000;
      });
      return total;
  }

  public playFullSequence(melody: MelodyData, speed: number = 0.2) {
      const ctx = this.initCtx();
      melody.forEach((noteData, i) => {
          let notes: number[] = Array.isArray(noteData) ? noteData : (typeof noteData === 'object' ? (Array.isArray(noteData.notes) ? noteData.notes : [noteData.notes]) : [noteData]);
          notes.forEach(f => {
              if (f > 0) {
                  const osc = ctx.createOscillator(); const gain = ctx.createGain();
                  osc.frequency.setValueAtTime(f, ctx.currentTime + i * speed);
                  gain.gain.setValueAtTime(0.2, ctx.currentTime + i * speed);
                  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * speed + 0.4);
                  osc.connect(gain); gain.connect(this.masterGain!);
                  osc.start(ctx.currentTime + i * speed); osc.stop(ctx.currentTime + i * speed + 0.5);
              }
          });
      });
  }

  public analyzeFile(file: File, cs: number, ce: number, onProg?: any) { return FrequencyAnalyzer.analyzeFile(this.initCtx(), file, cs, ce, onProg); }
  public generateRandomMelody(length: number): MelodyData {
      const scale = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25];
      return Array.from({ length }, () => scale[Math.floor(Math.random() * scale.length)]);
  }
}

export const AUDIO = new AudioService();
